import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { SiteLayout } from "../components/site/SiteLayout";
import { AnimatedHero } from "../components/site/AnimatedHero";
import { IndustryGlyph } from "../components/site/IndustryGlyph";
import { Reveal } from "../lib/reveal";
import { useSeo, orgJsonLd, faqJsonLd } from "../lib/seo";
import {
  services,
  industries,
  whyWald,
  trackRecord,
  productAgriBrain360,
  productCertisfy,
  productWaldInsights,
  productWaldFlow,
  homeFaqs,
} from "../lib/content";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CAL_URL = process.env.REACT_APP_CAL_URL || "/contact";

export default function HomePage() {
  useSeo({
    title: "Agricultural Software Development & AgriTech Partner in South Africa",
    description:
      "Wald Enterprise is a South African agricultural technology partner delivering agricultural software development, agriculture AI solutions, farm and agricultural ERP systems, automation and digital transformation for exporters, packhouses and regulated agricultural operations.",
    path: "/",
    jsonLd: [orgJsonLd, faqJsonLd(homeFaqs)],
  });

  const [posts, setPosts] = React.useState([]);
  React.useEffect(() => {
    axios.get(`${API}/insights?limit=3`).then((r) => setPosts(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  return (
    <SiteLayout>
      {/* HERO — animated abstract, no photography */}
      <section className="relative overflow-hidden border-b border-border bg-background min-h-[86vh] flex items-center">
        <AnimatedHero className="absolute inset-0 w-full h-full text-[color:var(--wald-slate)] dark:text-[color:var(--wald-mist)] pointer-events-none" />
        <div className="container-wald relative py-24 md:py-32">
          <div className="max-w-4xl">
            <Reveal>
              <div className="eyebrow mb-6">Wald Enterprise</div>
            </Reveal>
            <Reveal delay={80}>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-[72px] leading-[1.02] tracking-tight">
                Built for Complex Business.
              </h1>
            </Reveal>
            <Reveal delay={160}>
              <div className="mt-5 font-display text-xl sm:text-2xl lg:text-3xl text-[color:var(--wald-copper)] tracking-tight">
                Technology Partner for Agricultural Export.
              </div>
            </Reveal>
            <Reveal delay={240}>
              <p className="mt-8 text-base md:text-lg max-w-2xl opacity-80 leading-relaxed">
                Wald Enterprise is an AgriTech partner helping South African agricultural exporters,
                packhouses and regulated organisations modernise operations through agriculture AI solutions,
                agricultural ERP systems, automation and digital transformation.
              </p>
            </Reveal>
            <Reveal delay={320}>
              <div className="mt-10 flex flex-wrap gap-3">
                <a href={CAL_URL} target="_blank" rel="noreferrer noopener" data-testid="hero-cta-primary" className="btn-copper">
                  Book a discovery call <ArrowUpRight size={16} strokeWidth={2} />
                </a>
                <Link data-testid="hero-cta-secondary" to="/services" className="btn-outline">
                  Explore services <ArrowRight size={16} strokeWidth={2} />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="border-b border-border">
        <div className="container-wald py-24">
          <div className="grid grid-cols-12 gap-8 mb-14">
            <div className="col-span-12 md:col-span-6">
              <div className="eyebrow mb-4">Services</div>
              <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-[1.05]">
                Four disciplines. One operational spine.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-5 md:col-start-8 flex md:items-end">
              <p className="text-base md:text-lg opacity-75 max-w-lg leading-relaxed">
                Engage Wald for one discipline, or bring us in as a full technology partner across the
                operation.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-border">
            {services.map((s) => (
              <Reveal key={s.slug} className="border-b border-r border-border p-8 md:p-10">
                <Link to={`/services/${s.slug}`} data-testid={`home-service-${s.slug}`} className="block group">
                  <div className="flex items-baseline gap-4 mb-6">
                    <span className="font-mono-tab text-xs opacity-50">{s.number}</span>
                    <div className="h-px flex-1 bg-border" />
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl mb-3 tracking-tight">{s.title}</h3>
                  <p className="text-base opacity-90 mb-2">{s.tagline}</p>
                  <p className="text-sm opacity-70 leading-relaxed max-w-md">{s.summary}</p>
                  <div className="mt-6 inline-flex items-center gap-1.5 copper-link text-sm">
                    Read more <ArrowRight size={14} strokeWidth={2} />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="border-b border-border bg-card">
        <div className="container-wald py-24">
          <div className="grid grid-cols-12 gap-8 mb-14">
            <div className="col-span-12 md:col-span-7">
              <div className="eyebrow mb-4">Industries</div>
              <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-[1.05]">
                Deep focus across the agri export value chain.
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {industries.map((ind, i) => (
              <Reveal key={ind.slug} delay={i * 60}>
                <Link
                  to={`/industries/${ind.slug}`}
                  data-testid={`home-industry-${ind.slug}`}
                  className="wald-card block h-full"
                >
                  <div className="relative aspect-[4/3] bg-background border-b border-border overflow-hidden">
                    <IndustryGlyph slug={ind.slug} />
                    <div className="absolute top-3 left-3 font-mono-tab text-[10px] bg-background/95 px-2 py-0.5 border border-border">
                      {ind.number}
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="font-display text-base mb-2 tracking-tight">{ind.title}</div>
                    <p className="text-xs opacity-70 leading-relaxed line-clamp-3">{ind.summary}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="border-b border-border">
        <div className="container-wald py-24">
          <div className="eyebrow mb-4">Products</div>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-[1.05] mb-10 max-w-2xl">
            Our products.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 border-t border-l border-border">
            {[productAgriBrain360, productCertisfy, productWaldInsights, productWaldFlow].map((p) => (
              <Link
                key={p.slug}
                to={`/products/${p.slug}`}
                data-testid={`home-product-${p.slug}`}
                className="border-r border-b border-border p-6 hover:bg-card transition-colors"
              >
                <div className="font-display text-base tracking-tight mb-2">{p.title}</div>
                <p className="text-xs opacity-75 leading-relaxed mb-4">{p.tagline}</p>
                <span className="inline-flex items-center gap-1 text-xs text-[color:var(--wald-copper)]">
                  Explore <ArrowRight size={12} strokeWidth={2} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY WALD */}
      <section className="border-b border-border bg-card">
        <div className="container-wald py-24">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-4">
              <div className="eyebrow mb-4">Why Wald</div>
              <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-[1.05]">
                A partner built for one job.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-10">
                {whyWald.map((w, i) => (
                  <Reveal delay={i * 60} key={w.title}>
                    <div>
                      <div className="font-mono-tab text-xs opacity-50 mb-3">0{i + 1}</div>
                      <div className="font-display text-lg mb-2 tracking-tight">{w.title}</div>
                      <p className="text-sm opacity-80 leading-relaxed">{w.body}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRACK RECORD — Certisfy positioned strictly as a capability example */}
      <section className="border-b border-border">
        <div className="container-wald py-24">
          <div className="eyebrow mb-4">Track Record</div>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-[1.05] mb-4 max-w-3xl">
            Reference builds — capability, not client claims.
          </h2>
          <p className="max-w-2xl opacity-75 mb-12 leading-relaxed text-base">
            Wald has been building software since 2019. Our focus on agricultural export and regulated
            operations is new — this track record is framed as capability areas we can demonstrate in this
            niche, not as long-established client counts within it. Below is what our team has built.
          </p>
          <div className="border-t border-border">
            {trackRecord.map((t) => (
              <Reveal key={t.kind} className="border-b border-border py-8 grid grid-cols-12 gap-6 items-center">
                <div className="col-span-12 md:col-span-3">
                  {t.logo ? (
                    <img
                      src={t.logo}
                      alt={`${t.client} logo`}
                      className="h-10 w-auto object-contain dark:invert-0"
                      loading="lazy"
                    />
                  ) : (
                    <div className="font-display text-lg tracking-tight opacity-70">{t.client}</div>
                  )}
                </div>
                <div className="col-span-12 md:col-span-3 font-mono-tab text-xs opacity-60 uppercase tracking-wider">
                  {t.kind}
                </div>
                <div className="col-span-12 md:col-span-6 text-sm opacity-85 leading-relaxed">
                  {t.description}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INSIGHTS PREVIEW */}
      <section className="border-b border-border bg-card">
        <div className="container-wald py-24">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="eyebrow mb-4">Insights</div>
              <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-[1.05] max-w-xl">
                Thinking on agri, software and operations.
              </h2>
            </div>
            <Link data-testid="home-insights-all" to="/insights" className="btn-outline">
              All insights <ArrowRight size={14} strokeWidth={2} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(posts.length ? posts.slice(0, 3) : []).map((p) => (
              <Link
                key={p.id}
                to={`/insights/${p.slug}`}
                data-testid={`home-insight-${p.slug}`}
                className="wald-card p-7 block h-full bg-background"
              >
                <div className="eyebrow mb-4">{p.category}</div>
                <div className="font-display text-lg tracking-tight mb-3 leading-snug">{p.title}</div>
                <p className="text-sm opacity-75 leading-relaxed line-clamp-3">{p.excerpt}</p>
                <div className="mt-6 inline-flex items-center gap-1.5 copper-link text-sm">
                  Read <ArrowRight size={14} strokeWidth={2} />
                </div>
              </Link>
            ))}
            {!posts.length && (
              <div className="col-span-3 opacity-60 text-sm">Insights coming soon.</div>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-border">
        <div className="container-wald py-24">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-4">
              <div className="eyebrow mb-4">Common questions</div>
              <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-[1.05]">
                Frequently asked.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-8">
              <div className="border-t border-border">
                {homeFaqs.map((f, i) => (
                  <details key={f.q} className="border-b border-border py-5 group" data-testid={`home-faq-${i}`}>
                    <summary className="cursor-pointer flex items-center justify-between font-display text-lg tracking-tight list-none">
                      <span>{f.q}</span>
                      <span className="text-[color:var(--wald-copper)] font-mono-tab text-xl transition-transform group-open:rotate-45">+</span>
                    </summary>
                    <p className="mt-3 opacity-80 leading-relaxed text-sm max-w-2xl">{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-border relative overflow-hidden bg-background">
        <div className="absolute inset-0 wald-dot-bg opacity-40" aria-hidden="true" />
        <div className="container-wald py-24 relative">
          <div className="grid grid-cols-12 gap-8 items-end">
            <div className="col-span-12 md:col-span-8">
              <div className="eyebrow mb-4">Talk to Wald</div>
              <h2 className="font-display text-3xl md:text-6xl tracking-tight leading-[1.02] max-w-3xl">
                A 30-minute discovery call. No pitch deck.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-4 flex md:justify-end">
              <a href={CAL_URL} target="_blank" rel="noreferrer noopener" data-testid="home-final-cta" className="btn-copper">
                Book a discovery call <ArrowUpRight size={16} strokeWidth={2} />
              </a>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
